import React, { useEffect, useRef, useState } from 'react';
import '../styles/BlockTreePreview.css';

// Live picture of the trace tree being configured in Step 2 of the wizard.
// Pure render of `levels` — no fetch, no state — so it redraws on every keystroke.
//
// The Graph view runs left→right, but this lives in a sidebar: a 6-level chain laid out
// horizontally shrinks to unreadable. So depth runs TOP→BOTTOM here and siblings spread
// sideways — a chain (the common case) is then one narrow column that always fits, and a
// branch fans out visibly. A parent sits at the horizontal midpoint of its children.
// Nodes are HTML (ellipsis/wrapping come free); only the edges are SVG.

const NODE_W = 158;
const NODE_H = 30;    // a chip, not a card — the form is the UI, this is an annotation of it
const COL_GAP = 12;   // between siblings
const ROW_GAP = 26;   // between depths — the edges live in this band

function layout(nodes) {
  const childrenOf = (id) => nodes.filter(n => n.parent === id);
  const pos = {};
  let nextCol = 0;

  // Post-order: leaves take the next free column; a parent centres over its children.
  const place = (node) => {
    const kids = childrenOf(node.id);
    let col;
    if (kids.length === 0) {
      col = nextCol++;
    } else {
      const kidCols = kids.map(k => place(k));
      col = (kidCols[0] + kidCols[kidCols.length - 1]) / 2;
    }
    pos[node.id] = {
      x: col * (NODE_W + COL_GAP),
      y: (node.depth - 1) * (NODE_H + ROW_GAP)
    };
    return col;
  };

  const root = nodes.find(n => n.parent === null);
  if (root) place(root);

  const maxDepth = Math.max(...nodes.map(n => n.depth), 1);
  return {
    pos,
    width: Math.max(nextCol, 1) * (NODE_W + COL_GAP) - COL_GAP,
    height: maxDepth * (NODE_H + ROW_GAP) - ROW_GAP
  };
}

function BlockTreePreview({ nodes, activeId, onSelect }) {
  const scrollRef = useRef(null);
  const [avail, setAvail] = useState(0);   // usable width of the column, measured live

  // Keep every hook above the early return — a conditional hook count is what crashed the
  // graph view in v2.13.0.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setAvail(el.clientWidth));
    ro.observe(el);
    setAvail(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  if (!nodes.length) return null;

  const { pos, width, height } = layout(nodes);

  // Always fit — the panel never scrolls, so a wide tree (a level branching to 3 types) is
  // scaled down instead. Never zoom past 1×, so a small tree doesn't balloon. The floor stops
  // a pathological tree from becoming an unreadable smudge; that's the only case that clips.
  const scale = avail && width > avail ? Math.max(avail / width, 0.4) : 1;

  // Parent's bottom edge → child's top edge, curving through the gap between the depths.
  const edges = nodes
    .filter(n => n.parent && pos[n.parent])
    .map(n => {
      const from = pos[n.parent];
      const to = pos[n.id];
      const x1 = from.x + NODE_W / 2;
      const y1 = from.y + NODE_H;
      const x2 = to.x + NODE_W / 2;
      const y2 = to.y;
      const mid = y1 + (y2 - y1) / 2;
      return { id: n.id, d: `M ${x1} ${y1} C ${x1} ${mid}, ${x2} ${mid}, ${x2} ${y2}` };
    });

  return (
    <div className="tree-preview">
      <div className="tree-preview-scroll" ref={scrollRef}>
        {/* The scaled stage still occupies its scaled box, so the panel sizes to what's drawn */}
        <div
          className="tree-preview-fit"
          style={{ width: width * scale, height: height * scale }}
        >
        <div
          className="tree-preview-stage"
          style={{ width, height, transform: `scale(${scale})` }}
        >
          <svg className="tree-preview-edges" width={width} height={height}>
            {edges.map(e => (
              <path key={e.id} className="tp-edge" d={e.d} />
            ))}
          </svg>

          {nodes.map(n => (
            <button
              type="button"
              key={n.id}
              className={
                'tp-node' +
                (n.isRoot ? ' tp-root' : '') +
                (n.optional ? ' tp-optional' : '') +
                (n.id === activeId ? ' tp-active' : '') +
                (n.label ? '' : ' tp-empty')
              }
              style={{ left: pos[n.id].x, top: pos[n.id].y, width: NODE_W, height: NODE_H }}
              onClick={() => onSelect && onSelect(n.id)}
              title={n.title}
            >
              {/* The type is all a node says — the level name / filter id are already on the
                  form two inches to the left; repeating them here is what made it heavy. */}
              <span className="tp-node-label">{n.label || 'No type yet'}</span>
            </button>
          ))}
        </div>
        </div>
      </div>
    </div>
  );
}

export default BlockTreePreview;
